// Импортируем функцию переключения языка и переменную с переводами
import { switchLanguage, t, loadTranslations, getCurrentLanguageFromPath } from './i18n.js';
// Импортируем константы
import {
  WEBINAR_COUNTRIES_AMERICA,
  WEBINAR_URLS,
  CACHE_KEYS,
  CACHE_TTL,
  SCROLL_THRESHOLD,
  DISCOUNT,
  COUNTRIES_PRICES,
  COUNTRIES_PRICES_ORGANIZATIONS,
  RATING_TRUSTPILOT,
  RATING_APPLE_STORE,
  RATING_MICROSOFT_STORE,
  RATING_GOOGLE_PLAY,
  MOOZ_API_ORGANIZATIONS_PRICES
} from './constants.js';
// Импортируем контроллер блога
import { initBlog, initSocialShareLinks } from './blog-controller.js';
// Импортируем функции обновления рейтингов
import { updateTrustpilotRating, updateAppleStoreRating, updateMicrosoftStoreRating, updateGooglePlayRating } from './modules/ui.js';
// Импортируем модуль для hero видео
import { initHeroVideo } from './hero-video.js';
// Импортируем контроллер модальных окон
import { initModals, initAutoModalOneToOne, initAutoModalMusicToolkit, setLocationDataGetter } from './modal-controller.js';

// Глобальные переменные для геолокации
let userLocation = 'Unknown';
let countryCode = 'Unknown';
let isEuCountry = false; // Флаг принадлежности к ЕС
let isLocationLoading = true; // Флаг загрузки геолокации

// Импортируем функцию конвертации кода валюты в символ
import { currencyCodeToSymbol } from './currency-to-symbol.js';

// Геттер для получения данных геолокации (используется в modal-controller.js)
export function getLocationData() {
  return {
    userLocation,
    countryCode,
    isEuCountry,
    isLocationLoading
  };
}

// Глобальная переменная для состояния тоглера (true = годовой план, false = месячный план)
let isYearlyPlan = true;

// Глобальная переменная для состояния тоглера organizations (true = годовой план, false = месячный план)
let isYearlyPlanOrg = false;

// Глобальная переменная для хранения данных о ценах organizations
let organizationPricesData = null;


// Функция для получения геолокации пользователя с кэшированием
async function getUserLocation() {
  try {
    // Проверяем кэш
    const cached = localStorage.getItem(CACHE_KEYS.USER_LOCATION);
    if (cached) {
      const cachedData = JSON.parse(cached);
      const now = Date.now();
      
      // Если кэш не устарел, используем его
      if (now - cachedData.timestamp < CACHE_TTL) {
        userLocation = cachedData.location;
        countryCode = cachedData.countryCode;
        isEuCountry = cachedData.isEuCountry || false;
        isLocationLoading = false;
        // Обновляем цены на странице pricing
        updatePricingPrices();
        // Обновляем видимость элементов вебинаров в зависимости от страны
        updateWebinarElementsVisibility();
        return;
      }
    }
    
    // Показываем заглушки во время загрузки
    showPricingPlaceholders();
    
    // Инициализируем discount badge сразу (не зависит от страны)
    const discountBadge = document.querySelector('.discount-badge');
    if (discountBadge) {
      discountBadge.textContent = `-${DISCOUNT}%`;
    }
    updateDiscountText();
    
    // Получаем свежие данные
    const geoResponse = await fetch(
      "https://www.iplocate.io/api/lookup/?apikey=dd758d7b72ad55f84d8735c2353fa1fe"
    );
    const geoData = await geoResponse.json();
    
    // Формируем данные для сохранения
    userLocation = `${geoData.country}/${geoData.subdivision}/${geoData.city}`;
    countryCode = geoData.country_code || 'Unknown';
    isEuCountry = geoData.is_eu || false;
    isLocationLoading = false;
    
    // Сохраняем в кэш
    const cacheData = {
      location: userLocation,
      countryCode: countryCode,
      isEuCountry: isEuCountry,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEYS.USER_LOCATION, JSON.stringify(cacheData));
    
    // Обновляем цены на странице pricing
    updatePricingPrices();

    // Обновляем видимость элементов вебинаров в зависимости от страны
    updateWebinarElementsVisibility();

  } catch (error) {
    console.warn('Failed to get location:', error);
    isLocationLoading = false;
    
    // В случае ошибки пытаемся использовать кэш, даже если он устарел
    const cached = localStorage.getItem(CACHE_KEYS.USER_LOCATION);
    if (cached) {
      const cachedData = JSON.parse(cached);
      userLocation = cachedData.location;
      countryCode = cachedData.countryCode;
      isEuCountry = cachedData.isEuCountry || false;
      // Обновляем цены на странице pricing
      updatePricingPrices();
      // Обновляем видимость элементов вебинаров в зависимости от страны
      updateWebinarElementsVisibility();
    } else {
      // Если кэша нет, показываем дефолтные цены
      showDefaultPricing();
    }
  }
}

// Функция для получения цен для organizations с кэшированием
async function getOrganizationPrices() {
  try {
    // Проверяем кэш
    const cached = localStorage.getItem(CACHE_KEYS.ORG_PRICES);
    if (cached) {
      const cachedData = JSON.parse(cached);
      const now = Date.now();

      // Если кэш не устарел И countryCode совпадает, используем его
      if (now - cachedData.timestamp < CACHE_TTL && cachedData.countryCode === countryCode) {
        organizationPricesData = cachedData.prices;
        renderOrganizationPricing(organizationPricesData);
        return;
      }
    }

    // Показываем спинеры во время загрузки
    showOrganizationPricingPlaceholders();

    // Получаем свежие данные с API
    const response = await fetch(
      `${MOOZ_API_ORGANIZATIONS_PRICES}?country=${countryCode.toLowerCase()}`
    );
    const pricesData = await response.json();

    // Сохраняем данные
    organizationPricesData = pricesData;

    // Сохраняем в кэш вместе с countryCode
    const cacheData = {
      prices: pricesData,
      countryCode: countryCode,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEYS.ORG_PRICES, JSON.stringify(cacheData));

    // Обновляем цены на странице
    renderOrganizationPricing(organizationPricesData);

  } catch (error) {
    console.warn('Failed to get organization prices:', error);

    // В случае ошибки пытаемся использовать кэш, даже если он устарел
    const cached = localStorage.getItem(CACHE_KEYS.ORG_PRICES);
    if (cached) {
      const cachedData = JSON.parse(cached);
      organizationPricesData = cachedData.prices;
      renderOrganizationPricing(organizationPricesData);
    }
  }
}

// Функция для обновления ссылок на вебинар в зависимости от страны
function updateWebinarLinks() {
  // Определяем URL в зависимости от страны
  const webinarUrl = WEBINAR_COUNTRIES_AMERICA[countryCode] 
    ? WEBINAR_URLS.america 
    : WEBINAR_URLS.europe;
  
  // Обновляем все ссылки на вебинар на странице
  const webinarLinks = document.querySelectorAll('[data-webinar-link]');
  webinarLinks.forEach(link => {
    link.href = webinarUrl;
  });
}

// Универсальная функция для определения правильного значения display в зависимости от типа элемента
function getDisplayValueForElement(element) {
  const tagName = element.tagName.toLowerCase();
  // Для inline элементов используем соответствующие значения
  if (['span', 'a', 'strong', 'em', 'b', 'i', 'u'].includes(tagName)) {
    return 'inline';
  } else if (['li'].includes(tagName)) {
    return 'list-item';
  } else if (['table'].includes(tagName)) {
    return 'table';
  } else if (['tr'].includes(tagName)) {
    return 'table-row';
  } else if (['td', 'th'].includes(tagName)) {
    return 'table-cell';
  }
  // По умолчанию используем block
  return 'block';
}

// Универсальная функция для управления видимостью элементов с атрибутами data-webinar-america и data-webinar-other
// Показывает элементы с data-webinar-america для стран из WEBINAR_COUNTRIES_AMERICA (US, CA)
// Показывает элементы с data-webinar-other для остальных стран
function updateWebinarElementsVisibility() {
  // Проверяем, что countryCode определен и не равен 'Unknown'
  if (!countryCode || countryCode === 'Unknown') {
    return;
  }

  // Получаем все элементы с атрибутами data-webinar-america и data-webinar-other
  const elementsAmerica = document.querySelectorAll('[data-webinar-america]');
  const elementsOther = document.querySelectorAll('[data-webinar-other]');

  // Проверяем, находится ли страна в списке WEBINAR_COUNTRIES_AMERICA
  const isAmericaCountry = Boolean(WEBINAR_COUNTRIES_AMERICA[countryCode]);

  const bannerWrapper = document.querySelector('.gradient-banner-wrapper');

  if (isAmericaCountry) {
    elementsAmerica.forEach(element => {
      if (bannerWrapper && bannerWrapper.contains(element)) {
        element.classList.add('gradient-banner-visible');
      } else {
        const displayValue = getDisplayValueForElement(element);
        element.style.setProperty('display', displayValue, 'important');
      }
    });
    elementsOther.forEach(element => {
      if (bannerWrapper && bannerWrapper.contains(element)) {
        element.classList.remove('gradient-banner-visible');
      } else {
        element.style.display = 'none';
      }
    });
  } else {
    elementsOther.forEach(element => {
      if (bannerWrapper && bannerWrapper.contains(element)) {
        element.classList.add('gradient-banner-visible');
      } else {
        const displayValue = getDisplayValueForElement(element);
        element.style.setProperty('display', displayValue, 'important');
      }
    });
    elementsAmerica.forEach(element => {
      if (bannerWrapper && bannerWrapper.contains(element)) {
        element.classList.remove('gradient-banner-visible');
      } else {
        element.style.display = 'none';
      }
    });
  }
}

// Обработка скролла для хедера
function handleHeaderScroll() {
  const header = document.querySelector('.header');
  const SCROLL_HYSTERESIS = 20; // Разница между высотой скролла с стандартном состояниии с классом scrolled
  
  function updateHeaderStyle() {
    const isScrolled = header.classList.contains('scrolled');
    if (!isScrolled && window.scrollY > SCROLL_THRESHOLD) {
      header.classList.add('scrolled');
    } else if (isScrolled && window.scrollY < SCROLL_THRESHOLD - SCROLL_HYSTERESIS) {
      header.classList.remove('scrolled');
    }
  }
  
  // Обновляем стиль при скролле
  window.addEventListener('scroll', updateHeaderStyle);
  
  // Проверяем начальное состояние
  updateHeaderStyle();
}

// Обработка выпадающего меню выбора языка
function handleLanguageSelect() {
  const languageSelect = document.querySelector('.language-select');
  const languageIcon = document.querySelector('.language-select__icon');
  const languageWindow = document.querySelector('.language-select__window');
  const languageOptions = document.querySelectorAll('.language-select__option');

  if (!languageSelect || !languageIcon || !languageWindow || languageOptions.length === 0) {
    return;
  }

  // Перемещаем активный (выбранный) язык в начало списка
  const scrollContainer = languageWindow.querySelector('.language-select__scroll');
  const activeOption = languageWindow.querySelector('.language-select__option.active');
  if (scrollContainer && activeOption) {
    scrollContainer.prepend(activeOption);
  }

  // Открытие/закрытие меню
  function toggleLanguageMenu() {
    languageWindow.classList.toggle('open');
  }

  // Закрытие меню
  function closeLanguageMenu() {
    languageWindow.classList.remove('open');
  }

  // Обработка выбора языка
  function selectLanguage(event) {
    const option = event.currentTarget;
    const selectedLang = option.dataset.lang;
    
    // Закрываем меню
    closeLanguageMenu();
    
    // Переключаем язык
    switchLanguage(selectedLang);
  }

  // Обработчики событий
  languageIcon.addEventListener('click', (e) => {
    e.stopPropagation();
    toggleLanguageMenu();
  });

  // Клик по опциям языка
  languageOptions.forEach(option => {
    option.addEventListener('click', selectLanguage);
  });

  // Закрытие при клике вне меню
  document.addEventListener('click', (e) => {
    if (!languageSelect.contains(e.target)) {
      closeLanguageMenu();
    }
  });

  // Закрытие при нажатии Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeLanguageMenu();
    }
  });
}

// Инициализация Swiper для карусели отзывов
function initTestimonialSwiper() {
  const testimonialSwiper = new Swiper('.testimonial-swiper', {
    slidesPerView: 4,
    slidesPerGroup: 4,
    spaceBetween: 20,
    loop: false,
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
      bulletClass: 'swiper-pagination-bullet',
      bulletActiveClass: 'swiper-pagination-bullet-active',
    },
  });
}

// Управление ав��оматическим слайдером отзывов
function initReviewsSlider() {
  const sliderTrack = document.querySelector('.reviews-slider-track');
  const sliderContainer = document.querySelector('.reviews-slider-container');
  
  if (!sliderTrack || !sliderContainer) return;
  
  // Пауза при наведении мыши
  sliderContainer.addEventListener('mouseenter', () => {
    sliderTrack.style.animationPlayState = 'paused';
  });
  
  sliderContainer.addEventListener('mouseleave', () => {
    sliderTrack.style.animationPlayState = 'running';
  });
  
  // Пауза при фокусе на карточке (для доступности)
  const reviewCards = sliderContainer.querySelectorAll('.review-card');
  reviewCards.forEach(card => {
    card.addEventListener('focus', () => {
      sliderTrack.style.animationPlayState = 'paused';
    });
    
    card.addEventListener('blur', () => {
      sliderTrack.style.animationPlayState = 'running';
    });
  });
}



// Функция создания SVG спинера для цен
function createPricingSpinner(className = '') {
  const spinnerDiv = document.createElement('div');
  spinnerDiv.className = `pricing-spinner ${className}`;
  spinnerDiv.innerHTML = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'><radialGradient id='a10' cx='.66' fx='.66' cy='.3125' fy='.3125' gradientTransform='scale(1.5)'><stop offset='0' stop-color='#6DAE45'></stop><stop offset='.3' stop-color='#6DAE45' stop-opacity='.9'></stop><stop offset='.6' stop-color='#6DAE45' stop-opacity='.6'></stop><stop offset='.8' stop-color='#6DAE45' stop-opacity='.3'></stop><stop offset='1' stop-color='#6DAE45' stop-opacity='0'></stop></radialGradient><circle transform-origin='center' fill='none' stroke='url(#a10)' stroke-width='20' stroke-linecap='round' stroke-dasharray='200 1000' stroke-dashoffset='0' cx='100' cy='100' r='70'><animateTransform type='rotate' attributeName='transform' calcMode='spline' dur='2' values='360;0' keyTimes='0;1' keySplines='0 0 1 1' repeatCount='indefinite'></animateTransform></circle><circle transform-origin='center' fill='none' opacity='.2' stroke='#6DAE45' stroke-width='20' stroke-linecap='round' cx='100' cy='100' r='70'></circle></svg>`;
  return spinnerDiv;
}

// Функция показа спинеров для цен organizations во время загрузки
function showOrganizationPricingPlaceholders() {
  // .card-price, .card-period, .card-note скрыты через opacity: 0 в HTML до получения данных

  // Показываем спинер в .pricing-comparison-school-starter .pricing-plan-price
  const starterTablePrice = document.querySelector('.pricing-comparison-school-starter .pricing-plan-price');
  if (starterTablePrice) {
    starterTablePrice.style.display = 'none';
    const existingSpinner = starterTablePrice.parentElement.querySelector('.pricing-spinner');
    if (existingSpinner) {
      existingSpinner.remove();
    }
    starterTablePrice.parentElement.appendChild(createPricingSpinner());
  }

  // Показываем спинер в .pricing-comparison-school-pro .pricing-plan-price
  const proTablePrice = document.querySelector('.pricing-comparison-school-pro .pricing-plan-price');
  if (proTablePrice) {
    proTablePrice.style.display = 'none';
    const existingSpinner = proTablePrice.parentElement.querySelector('.pricing-spinner');
    if (existingSpinner) {
      existingSpinner.remove();
    }
    proTablePrice.parentElement.appendChild(createPricingSpinner());
  }

  // Показываем спинер в .discount-badge-organizations
  const discountBadge = document.querySelector('.discount-badge-organizations');
  if (discountBadge) {
    discountBadge.textContent = '';
    const existingSpinner = discountBadge.querySelector('.pricing-spinner');
    if (existingSpinner) {
      existingSpinner.remove();
    }
    discountBadge.appendChild(createPricingSpinner('spinner-badge'));
  }
}

// Функция рендеринга цен для organizations
function renderOrganizationPricing(pricesData) {
  if (!pricesData || !Array.isArray(pricesData)) {
    console.error('No valid pricing data provided for organizations');
    return;
  }

  // Парсим данные из массива
  const starterMonth = pricesData.find(p => p.tariffName === 'starter' && p.interval === 'month');
  const starterYear = pricesData.find(p => p.tariffName === 'starter' && p.interval === 'year');
  const proMonth = pricesData.find(p => p.tariffName === 'pro' && p.interval === 'month');
  const proYear = pricesData.find(p => p.tariffName === 'pro' && p.interval === 'year');

  // Определяем какие данные использовать в зависимости от состояния тоглера
  const starterData = isYearlyPlanOrg ? starterYear : starterMonth;
  const proData = isYearlyPlanOrg ? proYear : proMonth;

  if (!starterData || !proData) {
    console.error('Missing pricing data for starter or pro plans');
    return;
  }

  const currency = currencyCodeToSymbol(starterData.currency) || starterData.currency;

  // Обновляем discount badge (только для годового плана)
  const discountBadge = document.querySelector('.discount-badge-organizations');
  if (discountBadge) {
    // Удаляем спинер
    const spinner = discountBadge.querySelector('.pricing-spinner');
    if (spinner) {
      spinner.remove();
    }

    // Показываем скидку только для годового плана
    if (starterYear.discount) {
      discountBadge.textContent = `-${starterYear.discount}%`;
    } else {
      discountBadge.style.display = 'none';
    }
  }

  // Получаем тексты периодов из локализации
  const periodText = t.pricing?.organizations?.cards?.schoolStarter?.perTeacher || 'per teacher';

  const periodTextPro = isYearlyPlanOrg
    ? (t.pricing?.organizations?.cards?.schoolPro?.periodYearly || 'year')
    : (t.pricing?.organizations?.cards?.schoolPro?.periodMonthly || 'month');

  // Обновляем карточку School Starter
  const starterCardPrice = document.querySelector('.school-starter-card .card-price');
  if (starterCardPrice) {
    // Обновляем цену используя шаблон из локализации
    if (t.pricing?.organizations?.cards?.schoolStarter?.price) {
      const priceText = t.pricing.organizations.cards.schoolStarter.price
        .replace('{currency}', currency)
        .replace('{price}', proData.price);
      starterCardPrice.textContent = priceText;
    } else {
      starterCardPrice.textContent = `${currency}${proData.price}`;
    }
    starterCardPrice.style.opacity = '1';
  }

  const starterCardPeriod = document.querySelector('.school-starter-card .card-period');
  if (starterCardPeriod) {
    starterCardPeriod.textContent = ` / ${periodText}`;
    starterCardPeriod.style.opacity = '1';
  }

  // Обновляем card-note для starter (если есть)
  const starterCardNote = document.querySelector('.school-starter-card .card-note');
  if (starterCardNote && t.pricing?.organizations?.cards?.schoolStarter?.noteTeacher) {
    const noteText = t.pricing.organizations.cards.schoolStarter.noteTeacher
      .replace('{currency}', currency)
      .replace('{price}', proData.price)
      .replace('{period}', periodText);
    starterCardNote.textContent = noteText;
    starterCardNote.style.opacity = '1';
  }

  // Обновляем карточку School Pro
  const proCardPrice = document.querySelector('.school-pro-card .card-price');
  if (proCardPrice) {
    // Обновляем цену используя шаблон из локализации
    if (t.pricing?.organizations?.cards?.schoolPro?.price) {
      const priceText = t.pricing.organizations.cards.schoolPro.price
        .replace('{currency}', currency)
        .replace('{price}', proData.price);
      proCardPrice.textContent = priceText;
    } else {
      proCardPrice.textContent = `${currency}${proData.price}`;
    }
    proCardPrice.style.opacity = '1';
  }

  const proCardPeriod = document.querySelector('.school-pro-card .card-period');
  if (proCardPeriod) {
    proCardPeriod.textContent = ` / ${periodTextPro}`;
    proCardPeriod.style.opacity = '1';
  }

  // Обновляем card-note для pro (если есть)
  const proCardNote = document.querySelector('.school-pro-card .card-note');
  if (proCardNote && t.pricing?.organizations?.cards?.schoolPro?.note) {
    const noteText = t.pricing.organizations.cards.schoolPro.note
      .replace('{currency}', currency)
      .replace('{price}', proData.price)
      .replace('{period}', periodTextPro);
    proCardNote.textContent = noteText;
    proCardNote.style.opacity = '1';
  }

  // Обновляем таблицу сравнения - School Starter
  const starterTablePrice = document.querySelector('.pricing-comparison-school-starter .pricing-plan-price');
  if (starterTablePrice) {
    // Удаляем спинер из родителя
    const spinner = starterTablePrice.parentElement.querySelector('.pricing-spinner');
    if (spinner) {
      spinner.remove();
    }

    // Формируем текст с ценой и периодом
    const tablePeriodText = t.pricing?.organizations?.comparison?.plans?.schoolStarter?.perTeacher || 'per teacher';

    if (t.pricing?.organizations?.comparison?.plans?.schoolStarter?.price) {
      const priceText = t.pricing.organizations.comparison.plans.schoolStarter.price
        .replace('{currency}', currency)
        .replace('{price}', proData.price);
      starterTablePrice.textContent = priceText + ` / ${tablePeriodText}`;
    } else {
      starterTablePrice.textContent = `${currency}${proData.price}/${tablePeriodText}`;
    }
    starterTablePrice.style.display = '';
  }

  // Обновляем таблицу сравнения - School Pro
  const proTablePrice = document.querySelector('.pricing-comparison-school-pro .pricing-plan-price');
  if (proTablePrice) {
    // Удаляем спинер из родителя
    const spinner = proTablePrice.parentElement.querySelector('.pricing-spinner');
    if (spinner) {
      spinner.remove();
    }

    // Формируем текст с ценой и периодом
    const tablePeriodTextPro = isYearlyPlanOrg
      ? (t.pricing?.organizations?.comparison?.plans?.schoolPro?.periodYearly || 'year')
      : (t.pricing?.organizations?.comparison?.plans?.schoolPro?.periodMonthly || 'month');

    if (t.pricing?.organizations?.comparison?.plans?.schoolPro?.price) {
      const priceText = t.pricing.organizations.comparison.plans.schoolPro.price
        .replace('{currency}', currency)
        .replace('{price}', proData.price);
      proTablePrice.textContent = priceText + ` / ${tablePeriodTextPro}`;
    } else {
      proTablePrice.textContent = `${currency}${proData.price}/${tablePeriodTextPro}`;
    }
    proTablePrice.style.display = '';
  }
}

// Функция показа спинеров для цен во время загрузки
function showPricingPlaceholders() {
  // .price-display и .simple-price скрыты через opacity: 0 в HTML до получения данных
}

// Функция вычисления месячной цены из годовой
function calculateMonthlyPrice(yearPrice) {
  return (parseFloat(yearPrice.replace(/,/g, '')) / 12).toFixed(2);
}

// Универсальная функция для обновления всех элементов с ценами
function renderPricing(pricesData) {
  if (!pricesData) {
    console.error('No pricing data provided');
    return;
  }
  
  // Вычисляем цену за месяц при годовой подписке
  const monthlyPrice = calculateMonthlyPrice(pricesData.year);

  const priceDisplay = document.querySelector('.price-display');
  const oldPrice = document.querySelector('.old-price');
  const newPrice = document.querySelector('.new-price');

  if (isYearlyPlan) {
    // Годовой план: показываем скидку
    if (oldPrice) {
      oldPrice.textContent = `${pricesData.currency}${pricesData.nextMonth}`;
      oldPrice.classList.remove('price-loading');
    }
    if (newPrice) {
      newPrice.textContent = `${pricesData.currency}${monthlyPrice}`;
      newPrice.classList.remove('price-loading');
    }
  } else {
    // Месячный план: скрываем старую цену, показываем месячную в new-price
    if (oldPrice) {
      oldPrice.style.display = 'none';
    }
    if (newPrice) {
      newPrice.textContent = `${pricesData.currency}${pricesData.nextMonth}`;
      newPrice.classList.remove('price-loading');
    }
  }

  if (priceDisplay) {
    priceDisplay.style.opacity = '1';
  }

  const simplePrice = document.querySelector('.simple-price');

  // Обновляем все price-amount элементы (start) - цена за месяц при стартовом плане
  const priceAmounts = document.querySelectorAll('.price-amount.start-price');
  priceAmounts.forEach(priceAmount => {
    priceAmount.textContent = `${pricesData.currency}${pricesData.start}`;
    priceAmount.classList.remove('price-loading');
  });

  if (simplePrice) {
    simplePrice.style.opacity = '1';
  }
  
  // Определяем актуальную цену для Professional плана в зависимости от тоглера
  const professionalPrice = isYearlyPlan ? monthlyPrice : pricesData.nextMonth;
  
  // Обновляем pricing-plan-price-professional
  const pricingPlanPriceProfessional = document.querySelector('.pricing-plan-price-professional');
  if (pricingPlanPriceProfessional && t.pricing?.comparison?.plans?.professional?.price) {
    const professionalPriceText = t.pricing.comparison.plans.professional.price
      .replace('{currency}', pricesData.currency)
      .replace('{price}', professionalPrice);
    pricingPlanPriceProfessional.textContent = professionalPriceText;
  }
  
  // Обновляем pricing-plan-price-advanced с ценой стартового тарифа
  const pricingPlanPriceAdvanced = document.querySelector('.pricing-plan-price-advanced');
  if (pricingPlanPriceAdvanced && t.pricing?.comparison?.plans?.advanced?.price) {
    const advancedPriceText = t.pricing.comparison.plans.advanced.price
      .replace('{currency}', pricesData.currency)
      .replace('{price}', pricesData.start);
    pricingPlanPriceAdvanced.textContent = advancedPriceText;
  }
  
  // Обновляем trial-note с актуальной валютой и ценой (та же что показывается в new-price)
  const trialNote = document.querySelector('.trial-note-professional');
  if (trialNote && t.pricing?.cards?.professional?.trialNote && !isYearlyPlan) {
    const trialNoteText = t.pricing.cards.professional.trialNote
      .replace('{currency}', pricesData.currency)
      .replace('{price}', professionalPrice);
    trialNote.textContent = trialNoteText;
  } else if (trialNote && t.pricing?.cards?.professional?.trialNoteYearly && isYearlyPlan) {
    const trialNoteText = t.pricing.cards.professional.trialNoteYearly
      .replace('{currency}', pricesData.currency)
      .replace('{price}', professionalPrice);
    trialNote.textContent = trialNoteText;
  }
  
  // Обновляем trial-note-advanced с ценой стартового тарифа
  const trialNoteAdvanced = document.querySelector('.trial-note-advanced');
  if (trialNoteAdvanced && t.pricing?.cards?.advanced?.trialNote) {
    const trialNoteAdvancedText = t.pricing.cards.advanced.trialNote
      .replace('{currency}', pricesData.currency)
      .replace('{price}', pricesData.start);
    trialNoteAdvanced.textContent = trialNoteAdvancedText;
  }
}

// Функция показа дефолтных цен при ошибке
function showDefaultPricing() {
  const defaultPrices = COUNTRIES_PRICES['_'];
  
  if (!defaultPrices) {
    console.error('Default pricing not found');
    return;
  }
  
  renderPricing(defaultPrices);
}

// Функция обновления цен в зависимости от страны пользователя
function updatePricingPrices() {
  
  // Определяем ключ для поиска цен
  let priceKey = countryCode;
  
  // Если страна в ЕС и нет отдельных цен для неё, используем EU
  if (isEuCountry && !COUNTRIES_PRICES[countryCode]) {
    priceKey = 'EU';
  }
  
  // Получаем данные о ценах для страны пользователя
  const countryPrices = COUNTRIES_PRICES[priceKey] || COUNTRIES_PRICES['_'];
  
  if (!countryPrices) {
    console.warn('No pricing data found for country:', countryCode);
    showDefaultPricing();
    return;
  }
  
  // Используем универсальную функцию для обновления цен
  renderPricing(countryPrices);
}

// Функция обновления года в копирайте
function updateCopyrightYear() {
  const yearElement = document.getElementById('current-year');
  if (yearElement) {
    yearElement.textContent = new Date().getFullYear();
  }
}

// Функция инициализации тоглера цен
function initPriceToggle() {
  const toggleOptions = document.querySelectorAll('.toggle-option');
  
  if (toggleOptions.length === 0) {
    return; // Если тоглеров нет на странице, выходим
  }
  
  // Обработчик клика по тоглеру
  toggleOptions.forEach((option, index) => {
    option.addEventListener('click', () => {
      // Убираем активный класс со всех опций
      toggleOptions.forEach(opt => opt.classList.remove('active'));
      
      // Добавляем активный класс к выбранной опции
      option.classList.add('active');
      
      // Обновляем состояние (первая опция = годовой план, вторая = месячный)
      isYearlyPlan = index === 0;
      
      // Обновляем отображение цен
      updatePriceDisplay();
    });
  });
}

// Функция для обновления количества запросов в зависимости от выбранного плана
function updateRequestsCount() {
  // Определяем количество запросов в зависимости от плана
  const requestsCount = isYearlyPlan ? 200 : 100;
  
  // Находим все элементы с текстом о запросах
  const featureTextElements = document.querySelectorAll('.feature-text');
  
  featureTextElements.forEach(element => {
    const text = element.textContent || element.innerText;
    
    // Сохраняем оригинальный шаблонный текст в data-атрибуте при первом обращении
    if (!element.dataset.originalText) {
      if (text.includes('{requests_count}')) {
        element.dataset.originalText = text;
      }
    }
    
    // Если у элемента есть сохраненный шаблонный текст, заменяем от него
    if (element.dataset.originalText) {
      element.textContent = element.dataset.originalText.replace('{requests_count}', requestsCount);
    }
    // Или если текст содержит шаблон (для первого раза)
    else if (text.includes('{requests_count}')) {
      element.dataset.originalText = text;
      element.textContent = text.replace('{requests_count}', requestsCount);
    }
  });
}

// Функция обновления отображения цен в зависимости от выбранного тоглера
function updatePriceDisplay() {
  const oldPriceElement = document.querySelector('.old-price-amount.old-price');
  const newPriceElement = document.querySelector('.new-price-amount.new-price');
  
  if (!oldPriceElement || !newPriceElement) {
    return; // Если элементы не найдены, выходим
  }
  
  if (isYearlyPlan) {
    // Годовой план: показываем скидку (old-price видна, new-price с пониженной ценой)
    oldPriceElement.style.display = '';
    // Цены обновятся через renderPricing когда будут доступны данные
  } else {
    // Месячный план: скрываем old-price, в new-price показываем месячную цену
    oldPriceElement.style.display = 'none';
    // Цены обновятся через renderPricing когда будут доступны данные
  }
  
  // Обновляем количество запросов
  updateRequestsCount();
  
  // Если у нас уже есть данные о ценах, обновляем их
  if (!isLocationLoading && countryCode !== 'Unknown') {
    updatePricingPrices();
  }
}

// Функция управления видимостью элементов feature-highlight
function updateFeatureHighlightVisibility() {
  // Проверяем, что мы на странице pricing-organizations
  if (!window.location.pathname.includes('pricing-organizations')) {
    return;
  }

  const featureHighlights = document.querySelectorAll('.pricing-section-organizations .feature-highlight');

  featureHighlights.forEach(element => {
    if (isYearlyPlanOrg) {
      // Годовой план: показываем элементы
      element.classList.remove('hidden-monthly');
    } else {
      // Месячный план: скрываем элементы
      element.classList.add('hidden-monthly');
    }
  });
}

// Функция обновления отображения цен organizations при переключении тоглера
function updateOrganizationPriceDisplay() {
  // Если у нас уже есть данные о ценах, обновляем их
  if (organizationPricesData) {
    renderOrganizationPricing(organizationPricesData);
  }

  // Обновляем видимость элементов feature-highlight
  updateFeatureHighlightVisibility();
}

// Функция инициализации тоглера цен для organizations
function initOrganizationPriceToggle() {
  // Проверяем, что мы на странице pricing-organizations
  if (!window.location.pathname.includes('pricing-organizations')) {
    return;
  }

  const toggleOptions = document.querySelectorAll('.pricing-section-organizations .toggle-option');

  if (toggleOptions.length === 0) {
    return; // Если тоглеров нет на странице, выходим
  }

  // Обработчик клика по тоглеру
  toggleOptions.forEach((option, index) => {
    option.addEventListener('click', () => {
      // Убираем активный класс со всех опций
      toggleOptions.forEach(opt => opt.classList.remove('active'));

      // Добавляем активный класс к выбранной опции
      option.classList.add('active');

      // Обновляем состояние (первая опция = месячный план, вторая = годовой)
      isYearlyPlanOrg = index === 1;

      // Обновляем отображение цен
      updateOrganizationPriceDisplay();
    });
  });
}

// FAQ accordion functionality
function initFAQAccordion() {
  const faqItems = document.querySelectorAll('.faq-item');

  faqItems.forEach(item => {
    const question = item.querySelector('.faq-question');

    question.addEventListener('click', () => {
      // Переключаем только текущий элемент
      item.classList.toggle('opened');
    });
  });
}

// Универсальная функция инициализации тултипов
function initTooltips() {
  // Поддерживаем как старые селекторы, так и новые универсальные
  const tooltipElements = document.querySelectorAll('[data-tooltip-key]');
  
  tooltipElements.forEach(element => {
    const tooltipKey = element.getAttribute('data-tooltip-key');
    const tooltipPosition = element.getAttribute('data-position') || 'top';
    let tooltip = null;
    let isVisible = false;
    
    // Создаем тултип
    function createTooltip() {
      if (tooltip) return;
      
      tooltip = document.createElement('div');
      tooltip.className = 'tooltip';
      tooltip.setAttribute('data-position', tooltipPosition);
      
      // Получаем текст из локализации
      const tooltipText = t.tooltips?.[tooltipKey] || tooltipKey;
      tooltip.textContent = tooltipText;
      element.appendChild(tooltip);
    }
    
    // Показываем тултип
    function showTooltip() {
      if (!tooltip) createTooltip();
      tooltip.classList.add('show');
      isVisible = true;
    }
    
    // Скрываем тултип
    function hideTooltip() {
      if (tooltip) {
        tooltip.classList.remove('show');
        isVisible = false;
      }
    }
    
    // Обработчики для десктопа (hover)
    element.addEventListener('mouseenter', showTooltip);
    element.addEventListener('mouseleave', hideTooltip);
    
    // Обработчики для мобильных устройств (touch)
    element.addEventListener('touchstart', (e) => {
      e.preventDefault();
      if (isVisible) {
        hideTooltip();
      } else {
        showTooltip();
      }
    });
    
    // Закрытие при клике вне тултипа
    document.addEventListener('click', (e) => {
      if (isVisible && !element.contains(e.target) && !tooltip.contains(e.target)) {
        hideTooltip();
      }
    });
  });
}

// Функция для обновления текста скидки
function updateDiscountText() {
  const saveTextElement = document.querySelector('.save-text');
  if (saveTextElement && t.pricing?.cards?.professional?.pricing?.saveText) {
    const saveText = t.pricing.cards.professional.pricing.saveText.replace('{discount}', DISCOUNT);
    saveTextElement.textContent = saveText;
  }

  const discountBadge = document.querySelector('.discount-badge');
  if (discountBadge) {
    discountBadge.textContent = `-${DISCOUNT}%`;
  }
}

// Определение ОС пользователя (для десктопов/планшетов)
function detectDesktopOS() {
  const ua = (navigator.userAgent || '').toLowerCase();
  const platform = (navigator.platform || '').toLowerCase();

  // Явные мобильные телефоны — не трогаем (для них отдельный сайт)
  const isMobilePhone = /(iphone|ipod|android).*mobile/.test(ua) || /mobile/.test(ua);
  if (isMobilePhone) return null;

  if (ua.includes('windows') || platform.includes('win')) return 'windows';
  // iPadOS может маскироваться под MacIntel — не считаем iPhone/iPod выше, iPad оставляем как macOS не будем
  if (ua.includes('macintosh') || ua.includes('mac os x') || platform.includes('mac')) return 'mac';
  if ((ua.includes('linux') || ua.includes('x11') || platform.includes('linux')) && !ua.includes('android')) return 'linux';

  return null;
}

// Применение видимости блоков по определенной ОС
function applyDownloadOSVisibility(osKey) {
  const section = document.querySelector('.download-hero-section');
  if (!section) return;

  // Сбрасываем предыдущие флаги
  section.classList.remove('os-active-mac', 'os-active-windows', 'os-active-linux');
  section.classList.add('os-ready');

  // Если ОС не определена — ничего не скрываем (все видны согласно CSS по умолчанию)
  if (!osKey) return;

  // Выставляем флаг активной ОС
  if (osKey === 'mac') section.classList.add('os-active-mac');
  if (osKey === 'windows') section.classList.add('os-active-windows');
  if (osKey === 'linux') section.classList.add('os-active-linux');
}

// Инициализация логики на странице загрузки
function initDownloadOSVisibility() {
  if (!window.location.pathname.includes('download')) return;
  const os = detectDesktopOS();
  applyDownloadOSVisibility(os);
}

// Функция для динамического масштабирования жесткой верстки
function initResponsiveScaling() {
  const DESIGN_WIDTH = 1440; // Ширина дизайна
  const MIN_SCALE = 0.3; // Минимальный масштаб
  const MAX_SCALE = 1; // Максимальный масштаб
  // const isFirefox = typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent || '');

  function clearScaling() {
    document.body.classList.remove('scaled');
    document.body.style.transform = '';
    document.body.style.zoom = '';
    document.body.style.height = '';
  }

  function applyZoom(scale) {
    document.body.classList.add('scaled');
    document.body.style.zoom = String(scale);
    document.body.style.transform = '';
    document.body.style.height = '';
  }

  // function applyTransform(scale, viewportHeight) {
  //   document.body.classList.add('scaled');
  //   document.body.style.zoom = '';
  //   document.body.style.transform = `scale(${scale})`;
  //   // Чтобы визуальная высота соответствовала вьюпорту при scale
  //   const scaledHeight = viewportHeight / scale;
  //   document.body.style.height = `${scaledHeight}px`;
  // }

  function updateScale() {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Вычисляем масштаб на основе ширины экрана
    let scale = viewportWidth / DESIGN_WIDTH;
    scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));

    if (viewportWidth < DESIGN_WIDTH) {
      applyZoom(scale);
      // Основной путь: zoom (не ломает sticky)
      // if (!isFirefox) {
      //   applyZoom(scale);
      // } else {
      //   // Фолбэк для Firefox: оставляем transform, sticky здесь не оживляем
      //   applyTransform(scale, viewportHeight);
      // }
    } else {
      clearScaling();
    }
  }

  // Первичная инициализация и обработчики
  updateScale();

  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(updateScale, 16); // ~60fps
  });

  window.addEventListener('orientationchange', () => {
    setTimeout(updateScale, 100);
  });
}

// Инициализация при загрузке страницы
document.addEventListener('DOMContentLoaded', async () => {
  // Ждем загрузки переводов
  await loadTranslations();

  // Обновляем текст скидки после загрузки переводов
  updateDiscountText();

  // Обновляем рейтинги из констант
  updateTrustpilotRating(RATING_TRUSTPILOT);
  updateAppleStoreRating(RATING_APPLE_STORE);
  updateMicrosoftStoreRating(RATING_MICROSOFT_STORE);
  updateGooglePlayRating(RATING_GOOGLE_PLAY);

  // Если мы на странице pricing, показываем заглушки сразу
  if (window.location.pathname.includes('pricing')) {
    showPricingPlaceholders();
  }

  // Если мы на странице pricing-organizations, показываем заглушки сразу
  if (window.location.pathname.includes('pricing-organizations')) {
    showOrganizationPricingPlaceholders();
  }

  // Получаем геолокацию пользователя
  await getUserLocation();
  
  // Если мы на странице pricing-organizations, получаем цены
  if (window.location.pathname.includes('pricing-organizations')) {
    await getOrganizationPrices();
  }

  // Обновляем ссылки на вебинар в зависимости от страны
  updateWebinarLinks();

  // Обновляем видимость элементов вебинаров в зависимости от страны
  updateWebinarElementsVisibility();

  handleHeaderScroll();
  handleLanguageSelect();
  initReviewsSlider();

  // Устанавливаем геттер для геолокации и функцию обновления ссылки перед инициализацией модальных окон
  setLocationDataGetter(getLocationData);

  initModals();
  updateCopyrightYear();
  initAutoModalOneToOne();
  initFAQAccordion();
  initTooltips();
  initPriceToggle();
  initOrganizationPriceToggle();
  // Устанавливаем начальное состояние видимости feature-highlight
  updateFeatureHighlightVisibility();
  initSocialShareLinks();

  // Обновляем количество запросов при загрузке страницы (только для начального состояния)
  updateRequestsCount();

  // Инициализируем блог
  initBlog();


  // Инициализируем Swiper только если он доступен
  if (typeof Swiper !== 'undefined') {
    initTestimonialSwiper();
  } else {
    // Если Swiper еще не загружен, ждем его загрузки
    window.addEventListener('load', () => {
      if (typeof Swiper !== 'undefined') {
        initTestimonialSwiper();
      }
    });
  }

  // Инициализация показа блоков по ОС на странице download
  initDownloadOSVisibility();

  // Инициализация масштабирования для жесткой верстки
  initResponsiveScaling();

  // Инициализация автоматического открытия модального окна Music Toolkit
  initAutoModalMusicToolkit();

  // Инициализация hero видео
  initHeroVideo();
});
